import React, { useEffect, useMemo, useState } from 'react';
import { getDocs, query, where, orderBy } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrencyIntl } from '../utils/currencyUtils';
import { getCollectionForCompany } from '../utils/dataAdapter';
import AppSelect from './shared/AppSelect';

const PRESET_OPTIONS = [
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'this-year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const isoDate = (date) => new Date(date).toISOString().split('T')[0];

const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  if (preset === 'this-week') {
    const start = new Date(now);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    return { start: startOfDay(start), end: endOfDay(now) };
  }

  if (preset === 'this-year') {
    return {
      start: startOfDay(new Date(now.getFullYear(), 0, 1)),
      end: endOfDay(now)
    };
  }

  return {
    start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: endOfDay(now)
  };
};

const parseNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const toDocDate = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw?.toDate === 'function') return raw.toDate();
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isDateInRange = (dateValue, rangeStart, rangeEnd) => {
  const dt = dateValue instanceof Date ? dateValue : toDocDate(dateValue);
  if (!dt) return false;
  return dt >= startOfDay(rangeStart) && dt <= endOfDay(rangeEnd);
};

const getExpenseRecordDate = (exp) => {
  if (exp.date) return toDocDate(exp.date);
  if (exp.expenseDate) return toDocDate(exp.expenseDate);
  if (exp.paymentDate) return toDocDate(exp.paymentDate);
  if (exp.month) {
    const parts = String(exp.month).trim().split('-');
    if (parts.length >= 2) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      if (!Number.isNaN(year) && !Number.isNaN(month)) return new Date(year, month - 1, 1);
    }
  }
  return null;
};

const expenseInRange = (exp, rangeStart, rangeEnd) => (
  isDateInRange(getExpenseRecordDate(exp), rangeStart, rangeEnd)
);

const getExpenseAmount = (exp) => parseNumber(exp.amount || exp.totalAmount || exp.value || 0);

const isSalaryExpenseGroup = (exp) => String(exp.group || '').toLowerCase() === 'salaries';

const fetchCollectionInDateRange = async (db, companyCtx, collectionName, dateFields, rangeStart, rangeEnd) => {
  const colRef = getCollectionForCompany(db, companyCtx, collectionName);
  const startStr = isoDate(rangeStart);
  const endStr = isoDate(rangeEnd);

  for (const field of dateFields) {
    try {
      const rangeQuery = query(
        colRef,
        where(field, '>=', startStr),
        where(field, '<=', endStr),
        orderBy(field, 'desc')
      );
      const snap = await getDocs(rangeQuery);
      const rows = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      if (rows.length > 0) return rows;
    } catch (queryErr) {
      // try next date field or full-collection fallback
    }
  }

  try {
    const snap = await getDocs(colRef);
    return snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((row) => dateFields.some((field) => isDateInRange(row[field], rangeStart, rangeEnd)));
  } catch (err) {
    console.error(`BusinessAnalytics: failed loading ${collectionName}`, err);
    return [];
  }
};

const fetchIrregularPaymentsForAnalytics = async (db, companyCtx, rangeStart, rangeEnd) => {
  const colRef = getCollectionForCompany(db, companyCtx, 'irregularPayments');
  try {
    const snap = await getDocs(colRef);
    return snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((row) => isDateInRange(toDocDate(row.date), rangeStart, rangeEnd));
  } catch (err) {
    console.error('BusinessAnalytics: failed loading irregularPayments', err);
    return [];
  }
};

const fetchExpensesForAnalytics = async (db, companyCtx, rangeStart, rangeEnd) => {
  const colRef = getCollectionForCompany(db, companyCtx, 'expenses');
  // Load full collection and filter client-side so Timestamp/month-only dates are not missed
  // (indexed date queries can return only a subset, e.g. salary rows with string dates).
  try {
    const snap = await getDocs(colRef);
    return snap.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((exp) => expenseInRange(exp, rangeStart, rangeEnd));
  } catch (err) {
    console.error('BusinessAnalytics: failed loading expenses', err);
    return [];
  }
};

const getDirection = (rawValue) => {
  const value = String(rawValue || '').toLowerCase().trim();
  if (!value) return null;
  if (value.includes('receipt') || value.includes('inflow') || value.includes('credit') || value.includes('cash in')) return 'in';
  if (value.includes('payment') || value.includes('outflow') || value.includes('debit') || value.includes('cash out')) return 'out';
  return null;
};

const getBillDate = (bill, type = 'sales') => {
  const rawDate = type === 'sales'
    ? (bill.invoiceDate || bill.date)
    : (bill.billDate || bill.date);
  const dt = new Date(rawDate);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const getBillAmount = (bill) => parseNumber(bill.totalAmount || bill.amount || bill.total || 0);

const getBillRows = (bill) => {
  if (!bill) return [];
  if (Array.isArray(bill.rows) && bill.rows.length > 0) return bill.rows;
  if (Array.isArray(bill.items)) return bill.items;
  return [];
};

const normalizeItemKey = (row) => {
  return row.item || row.itemId || row.id || row.itemName || row.name || 'Unknown Item';
};

const normalizeItemQty = (row) => {
  return parseNumber(row.qty || row.quantity || row.nos || 0);
};

// Match StockReport row qty: qty field, else nos × length × height
const computeRowQty = (row) => {
  const direct = parseNumber(row?.qty);
  if (direct) return direct;
  return parseNumber(row?.nos) * (parseNumber(row?.length) || 1) * (parseNumber(row?.height) || 1);
};

const getBillRowsForStock = (bill) => {
  if (Array.isArray(bill?.rows)) return bill.rows;
  if (Array.isArray(bill?.items)) return bill.items;
  return [];
};

const buildAllTimeItemTransactionMap = (purchaseBills, salesBills, salesReturns, purchaseReturns) => {
  const itemTransactions = new Map();

  const ensureTrans = (itemId) => {
    if (!itemTransactions.has(itemId)) {
      itemTransactions.set(itemId, { purchased: 0, sold: 0 });
    }
    return itemTransactions.get(itemId);
  };

  purchaseBills.forEach((purchase) => {
    getBillRowsForStock(purchase).forEach((row) => {
      const itemId = row.item;
      if (!itemId) return;
      ensureTrans(itemId).purchased += computeRowQty(row);
    });
  });

  const applySaleRows = (sale, isReturn) => {
    getBillRowsForStock(sale).forEach((row) => {
      const itemId = row.item;
      if (!itemId) return;
      const qty = computeRowQty(row);
      const trans = ensureTrans(itemId);
      if (isReturn) trans.sold -= qty;
      else trans.sold += qty;
    });
  };

  salesBills.forEach((sale) => {
    const isReturn = sale.docType === 'salesReturn' || sale.isReturn;
    applySaleRows(sale, isReturn);
  });

  salesReturns.forEach((saleReturn) => {
    applySaleRows(saleReturn, true);
  });

  purchaseReturns.forEach((purchaseReturn) => {
    getBillRowsForStock(purchaseReturn).forEach((row) => {
      const itemId = row.item;
      if (!itemId) return;
      ensureTrans(itemId).purchased -= computeRowQty(row);
    });
  });

  return itemTransactions;
};

const resolveItemInHand = (item, trans, stockByItemId = {}) => {
  const hasBillActivity = trans.purchased !== 0 || trans.sold !== 0;
  if (hasBillActivity) {
    return calculateAllTimeInHand(item, trans);
  }
  const liveStock = stockByItemId[item.id];
  if (liveStock !== undefined && liveStock !== null) {
    return Math.round(parseNumber(liveStock) * 100) / 100;
  }
  return calculateAllTimeInHand(item, trans);
};

const calculateAllTimeInHand = (item, trans) => {
  const inHand = parseNumber(item.openingStock) + trans.purchased - trans.sold;
  return Math.round(inHand * 100) / 100;
};

const getStockAlertStatus = (inHand, reorderLevel) => {
  if (inHand < 0) return 'negative';
  if (inHand <= reorderLevel) return 'low';
  return 'normal';
};

const normalizeReorderLevel = (item) => {
  const configured = parseNumber(item.reorderLevel || item.lowStockAlert || item.minStock || item.minStockLevel || 0);
  return configured > 0 ? configured : 10; // align with StockReport fallback behavior
};

const getTrendLabel = (date, mode) => {
  if (mode === 'this-week') {
    return date.toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return date.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const SimpleBar = ({ value, maxValue, color = 'bg-blue-600' }) => {
  const widthPercent = maxValue > 0 ? Math.max(6, Math.round((value / maxValue) * 100)) : 0;
  return (
    <div className="w-full bg-gray-100 rounded h-2">
      <div className={`${color} h-2 rounded`} style={{ width: `${widthPercent}%` }} />
    </div>
  );
};

const SimpleVerticalBars = ({ data = [], valueKey, labelKey, color = 'from-blue-500 to-indigo-600', formatter = (v) => v }) => {
  const maxValue = Math.max(...data.map((d) => parseNumber(d[valueKey] || 0)), 0);
  const chartHeightPx = 150;
  const minBarPx = 12;
  return (
    <div className="h-56 flex items-end gap-2">
      {data.map((item, index) => {
        const value = parseNumber(item[valueKey] || 0);
        const barPx = maxValue > 0 ? Math.max(minBarPx, Math.round((value / maxValue) * chartHeightPx)) : 0;
        return (
          <div key={`${item[labelKey]}-${index}`} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full">
            <span className="text-[10px] font-semibold text-gray-600 mb-1 truncate max-w-full" title={String(formatter(value))}>
              {formatter(value)}
            </span>
            <div className="w-full h-40 flex items-end">
              <div
                className={`w-full bg-gradient-to-t ${color} rounded-t-md shadow`}
                style={{ height: `${barPx}px` }}
                title={`${item[labelKey]}: ${formatter(value)}`}
              />
            </div>
            <span className="text-[10px] text-gray-600 mt-2 truncate max-w-full">{item[labelKey]}</span>
          </div>
        );
      })}
    </div>
  );
};

const KpiCard = ({ title, value, tone = 'blue', hint }) => {
  const toneMap = {
    blue: 'from-blue-100 via-indigo-100 to-purple-100 text-blue-900 border-blue-200',
    green: 'from-emerald-100 via-teal-100 to-cyan-100 text-emerald-900 border-emerald-200',
    purple: 'from-purple-100 via-fuchsia-100 to-indigo-100 text-purple-900 border-purple-200',
    orange: 'from-amber-100 via-orange-100 to-rose-100 text-amber-900 border-amber-200',
    red: 'from-red-100 via-orange-100 to-rose-100 text-red-900 border-red-200',
    teal: 'from-cyan-100 via-sky-100 to-blue-100 text-cyan-900 border-cyan-200'
  };

  return (
    <div className={`rounded-xl border p-4 bg-gradient-to-br shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 ${toneMap[tone] || toneMap.blue}`}>
      <p className="text-xs font-medium uppercase tracking-wide opacity-80">{title}</p>
      <p className="mt-2 text-xl font-bold">{value}</p>
      {hint ? <p className="mt-1 text-xs opacity-80">{hint}</p> : null}
    </div>
  );
};

const BusinessAnalytics = ({ db, userId, isAuthReady, appId }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const { currency: companyCurrency } = useCompanyCurrency();

  const [preset, setPreset] = useState('this-month');
  const [customStart, setCustomStart] = useState(() => isoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customEnd, setCustomEnd] = useState(() => isoDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [visibleMostSoldCount, setVisibleMostSoldCount] = useState(8);
  const [visibleTopSellingChartCount, setVisibleTopSellingChartCount] = useState(6);
  const [visibleTopPartiesCount, setVisibleTopPartiesCount] = useState(8);
  const [visibleFastMovingCount, setVisibleFastMovingCount] = useState(5);
  const [visibleLowStockCount, setVisibleLowStockCount] = useState(8);
  const [itemsSortBy, setItemsSortBy] = useState('qty');
  const [itemsSortOrder, setItemsSortOrder] = useState('desc');
  const [itemsTablePageSize, setItemsTablePageSize] = useState(10);
  const [itemsTablePage, setItemsTablePage] = useState(1);
  const [topPartiesPageSize, setTopPartiesPageSize] = useState(10);
  const [topPartiesPage, setTopPartiesPage] = useState(1);
  const [lowStockPageSize, setLowStockPageSize] = useState(10);
  const [lowStockPage, setLowStockPage] = useState(1);
  const [itemStockSortOrder, setItemStockSortOrder] = useState('desc');

  const activeRange = useMemo(() => {
    if (preset === 'custom') {
      return {
        start: startOfDay(new Date(customStart)),
        end: endOfDay(new Date(customEnd))
      };
    }
    return getDateRangeFromPreset(preset);
  }, [preset, customStart, customEnd]);

  const formatMoney = (amount) => formatCurrencyIntl(parseNumber(amount), companyCurrency);
  const formatNumber = (value) => parseNumber(value).toFixed(2);
  const compactMoney = (amount) => {
    const n = parseNumber(amount);
    if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (Math.abs(n) >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
  };

  const sortedTopItems = useMemo(() => {
    if (!analytics?.topItems?.length) return [];
    const sorted = [...analytics.topItems];
    sorted.sort((a, b) => {
      let compare = 0;
      if (itemsSortBy === 'revenue') compare = parseNumber(a.revenue) - parseNumber(b.revenue);
      else if (itemsSortBy === 'name') compare = String(a.name || '').localeCompare(String(b.name || ''));
      else compare = parseNumber(a.qty) - parseNumber(b.qty);
      return itemsSortOrder === 'asc' ? compare : -compare;
    });
    return sorted;
  }, [analytics, itemsSortBy, itemsSortOrder]);

  const itemsTableTotalPages = useMemo(() => {
    if (!sortedTopItems.length) return 1;
    return Math.max(1, Math.ceil(sortedTopItems.length / itemsTablePageSize));
  }, [sortedTopItems, itemsTablePageSize]);

  const paginatedTopItems = useMemo(() => {
    const start = (itemsTablePage - 1) * itemsTablePageSize;
    const end = start + itemsTablePageSize;
    return sortedTopItems.slice(start, end);
  }, [sortedTopItems, itemsTablePage, itemsTablePageSize]);

  const itemsTableStartIndex = sortedTopItems.length ? ((itemsTablePage - 1) * itemsTablePageSize) + 1 : 0;
  const itemsTableEndIndex = Math.min(itemsTablePage * itemsTablePageSize, sortedTopItems.length);

  const getVisiblePageNumbers = () => {
    const total = itemsTableTotalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const start = Math.max(1, itemsTablePage - 2);
    const end = Math.min(total, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  };

  const topPartiesTotalPages = useMemo(() => {
    if (!analytics?.topParties?.length) return 1;
    return Math.max(1, Math.ceil(analytics.topParties.length / topPartiesPageSize));
  }, [analytics, topPartiesPageSize]);

  const paginatedTopParties = useMemo(() => {
    const list = analytics?.topParties || [];
    const start = (topPartiesPage - 1) * topPartiesPageSize;
    return list.slice(start, start + topPartiesPageSize);
  }, [analytics, topPartiesPage, topPartiesPageSize]);

  const sortedItemStockList = useMemo(() => {
    if (!analytics?.itemStockList?.length) return [];
    const sorted = [...analytics.itemStockList];
    sorted.sort((a, b) => {
      const compare = parseNumber(a.stock) - parseNumber(b.stock);
      return itemStockSortOrder === 'asc' ? compare : -compare;
    });
    return sorted;
  }, [analytics, itemStockSortOrder]);

  const itemStockTotalPages = useMemo(() => {
    if (!sortedItemStockList.length) return 1;
    return Math.max(1, Math.ceil(sortedItemStockList.length / lowStockPageSize));
  }, [sortedItemStockList, lowStockPageSize]);

  const paginatedItemStock = useMemo(() => {
    const start = (lowStockPage - 1) * lowStockPageSize;
    return sortedItemStockList.slice(start, start + lowStockPageSize);
  }, [sortedItemStockList, lowStockPage, lowStockPageSize]);

  const downloadCsv = () => {
    if (!analytics) return;
    const rows = [
      ['Metric', 'Value'],
      ['Range Start', analytics.range.start],
      ['Range End', analytics.range.end],
      ['Sales Revenue', analytics.kpis.totalSales],
      ['Purchase Cost', analytics.kpis.totalPurchases],
      ['Operating Expenses (incl salary/irregular)', analytics.kpis.totalExpenses],
      ['Gross Profit', analytics.kpis.grossProfit],
      ['Net Profit', analytics.kpis.netProfit],
      ['Cash In (Total)', analytics.kpis.receipts],
      ['Cash Out (Total)', analytics.kpis.payments],
      ['Cash In (Payments)', analytics.kpis.paymentsIn],
      ['Cash Out (Payments)', analytics.kpis.paymentsOut],
      ['Cash In (Employee Flow)', analytics.kpis.employeeInflow],
      ['Cash Out (Employee Flow)', analytics.kpis.employeeOutflow],
      ['Net Cashflow', analytics.kpis.netCashflow],
      [],
      ['Top Items', 'Qty Sold', 'Revenue']
    ];

    analytics.topItems.forEach((item) => {
      rows.push([item.name, formatNumber(item.qty), parseNumber(item.revenue)]);
    });

    rows.push([], ['Top Parties', 'Revenue']);
    analytics.topParties.forEach((party) => {
      rows.push([party.name, parseNumber(party.revenue)]);
    });

    const csv = rows.map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `business-analytics-${analytics.range.start}-to-${analytics.range.end}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!db || !userId || !isAuthReady || !companyId || !activeRange?.start || !activeRange?.end) return;

      setLoading(true);
      setError('');
      try {
        const startStr = isoDate(activeRange.start);
        const endStr = isoDate(activeRange.end);

        const salesQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills'),
          where('invoiceDate', '>=', startStr),
          where('invoiceDate', '<=', endStr),
          orderBy('invoiceDate', 'desc')
        );

        const purchasesQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills'),
          where('billDate', '>=', startStr),
          where('billDate', '<=', endStr),
          orderBy('billDate', 'desc')
        );

        const companyCtx = { companyId, appId, userId };

        const paymentsQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'payments'),
          where('paymentDate', '>=', startStr),
          where('paymentDate', '<=', endStr),
          orderBy('paymentDate', 'desc')
        );

        const employeeCashTxQuery = query(
          getCollectionForCompany(db, { companyId, appId, userId }, 'employeeCashTransactions'),
          where('date', '>=', startStr),
          where('date', '<=', endStr),
          orderBy('date', 'desc')
        );

        const [
          salesSnap,
          purchasesSnap,
          expenses,
          salaryPayments,
          irregularPaymentsRaw,
          paymentsSnap,
          employeeCashTxSnap,
          itemsSnap,
          allSalesSnap,
          allPurchasesSnap,
          allSalesReturnsSnap,
          purchaseReturnsSnap,
          stockSnap,
          partiesSnap
        ] = await Promise.all([
          getDocs(salesQuery),
          getDocs(purchasesQuery),
          fetchExpensesForAnalytics(db, companyCtx, activeRange.start, activeRange.end),
          fetchCollectionInDateRange(db, companyCtx, 'salaryPayments', ['date', 'paymentDate'], activeRange.start, activeRange.end),
          fetchIrregularPaymentsForAnalytics(db, companyCtx, activeRange.start, activeRange.end),
          getDocs(paymentsQuery),
          getDocs(employeeCashTxQuery),
          getDocs(getCollectionForCompany(db, companyCtx, 'items')),
          getDocs(getCollectionForCompany(db, companyCtx, 'salesBills')),
          getDocs(getCollectionForCompany(db, companyCtx, 'purchaseBills')),
          getDocs(getCollectionForCompany(db, companyCtx, 'salesReturns')),
          getDocs(getCollectionForCompany(db, companyCtx, 'purchaseReturns')),
          getDocs(getCollectionForCompany(db, companyCtx, 'stock')),
          getDocs(getCollectionForCompany(db, companyCtx, 'parties'))
        ]);

        const salesBills = salesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const purchaseBills = purchasesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        // Include all labour/advance payments in period (settled + unsettled), same as Cash Flow report.
        // P&L only counts unsettled as outstanding liability; analytics should reflect actual spend.
        const labourPayments = irregularPaymentsRaw;
        const payments = paymentsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const employeeCashTx = employeeCashTxSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const items = itemsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const allSalesBills = allSalesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const allPurchaseBills = allPurchasesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const allSalesReturns = allSalesReturnsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const allPurchaseReturns = purchaseReturnsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const stockByItemId = stockSnap.docs.reduce((acc, docSnap) => {
          acc[docSnap.id] = parseNumber(docSnap.data()?.itemQuantity || 0);
          return acc;
        }, {});
        const parties = partiesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const itemNameMap = items.reduce((acc, item) => {
          acc[item.id] = item.itemName || item.name || item.productName || item.id;
          return acc;
        }, {});

        const totalSales = salesBills.reduce((sum, bill) => sum + getBillAmount(bill), 0);
        const totalPurchases = purchaseBills.reduce((sum, bill) => sum + getBillAmount(bill), 0);
        const operatingExpenses = expenses.filter((exp) => !isSalaryExpenseGroup(exp));
        const legacySalaryInExpenses = expenses.filter(isSalaryExpenseGroup);

        const normalExpensesTotal = operatingExpenses.reduce((sum, exp) => sum + getExpenseAmount(exp), 0);
        const salaryPaymentsTotal = salaryPayments.reduce((sum, row) => (
          sum + parseNumber(row.netAmount || row.totalEarnings || row.total || row.amount || 0)
        ), 0);
        const legacySalaryTotal = legacySalaryInExpenses.reduce((sum, exp) => sum + getExpenseAmount(exp), 0);
        const salaryExpensesTotal = salaryPaymentsTotal + legacySalaryTotal;
        const labourExpensesTotal = labourPayments.reduce((sum, row) => (
          sum + parseNumber(row.amount || row.totalAmount || 0)
        ), 0);
        const totalExpenses = normalExpensesTotal + salaryExpensesTotal + labourExpensesTotal;
        const grossProfit = totalSales - totalPurchases;
        const netProfit = grossProfit - totalExpenses;

        const paymentsAgg = payments.reduce((acc, payment) => {
          const direction = getDirection(payment.type || payment.paymentType || payment.transactionType);
          const amount = parseNumber(payment.amount || payment.totalAmount || 0);
          if (direction === 'in') acc.in += amount;
          else if (direction === 'out') acc.out += amount;
          return acc;
        }, { in: 0, out: 0 });

        // Align with Employee Cash/UPI flow report logic (inflow/outflow from employeeCashTransactions)
        const employeeFlowAgg = employeeCashTx.reduce((acc, tx) => {
          const direction = getDirection(tx.transactionType || tx.type);
          const amount = parseNumber(tx.amount || tx.totalAmount || 0);
          if (direction === 'in') acc.in += amount;
          else if (direction === 'out') acc.out += amount;
          return acc;
        }, { in: 0, out: 0 });

        const totalCashIn = paymentsAgg.in + employeeFlowAgg.in;
        const totalCashOut = paymentsAgg.out + employeeFlowAgg.out;

        const itemStatsMap = {};
        const partyRevenueMap = {};
        salesBills.forEach((bill) => {
          const partyKey = bill.party || bill.partyId || bill.customerId || bill.partyName || 'Unknown Party';
          if (!partyRevenueMap[partyKey]) {
            partyRevenueMap[partyKey] = 0;
          }
          partyRevenueMap[partyKey] += getBillAmount(bill);

          getBillRows(bill).forEach((row) => {
            const key = normalizeItemKey(row);
            const qty = normalizeItemQty(row);
            const amount = parseNumber(row.amount || row.total || 0);
            if (!itemStatsMap[key]) {
              itemStatsMap[key] = { itemKey: key, qty: 0, revenue: 0 };
            }
            itemStatsMap[key].qty += qty;
            itemStatsMap[key].revenue += amount;
          });
        });

        const mostSoldItems = Object.values(itemStatsMap)
          .map((entry) => ({
            ...entry,
            name: itemNameMap[entry.itemKey] || entry.itemKey
          }))
          .sort((a, b) => b.qty - a.qty);

        const fastMovingItems = [...mostSoldItems].slice(0, 5);

        const topParties = Object.entries(partyRevenueMap)
          .map(([key, revenue]) => {
            const partyDoc = parties.find((p) => p.id === key);
            return {
              id: key,
              name: partyDoc?.firmName || partyDoc?.partyName || partyDoc?.name || key,
              revenue
            };
          })
          .sort((a, b) => b.revenue - a.revenue);

        const allTimeItemTransactions = buildAllTimeItemTransactionMap(
          allPurchaseBills,
          allSalesBills,
          allSalesReturns,
          allPurchaseReturns
        );

        const itemStockList = items
          .map((item) => {
            const trans = allTimeItemTransactions.get(item.id) || { purchased: 0, sold: 0 };
            const stock = resolveItemInHand(item, trans, stockByItemId);
            const reorderLevel = normalizeReorderLevel(item);
            const status = getStockAlertStatus(stock, reorderLevel);
            return {
              id: item.id,
              name: item.itemName || item.name || item.productName || item.id,
              stock,
              reorderLevel,
              status
            };
          });

        const trendMap = {};
        salesBills.forEach((bill) => {
          const dt = getBillDate(bill, 'sales');
          if (!dt) return;
          const key = getTrendLabel(dt, preset);
          if (!trendMap[key]) {
            trendMap[key] = { label: key, revenue: 0, bills: 0 };
          }
          trendMap[key].revenue += getBillAmount(bill);
          trendMap[key].bills += 1;
        });

        const revenueTrend = Object.values(trendMap).sort((a, b) => b.revenue - a.revenue);

        const getPeriodMetrics = async (mode) => {
          const periodRange = getDateRangeFromPreset(mode);
          const pStart = isoDate(periodRange.start);
          const pEnd = isoDate(periodRange.end);

          const periodSalesQuery = query(
            getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills'),
            where('invoiceDate', '>=', pStart),
            where('invoiceDate', '<=', pEnd),
            orderBy('invoiceDate', 'desc')
          );

          const periodSalesSnap = await getDocs(periodSalesQuery);
          const periodSales = periodSalesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          const revenue = periodSales.reduce((sum, bill) => sum + getBillAmount(bill), 0);

          const itemMap = {};
          periodSales.forEach((bill) => {
            getBillRows(bill).forEach((row) => {
              const key = normalizeItemKey(row);
              const qty = normalizeItemQty(row);
              itemMap[key] = (itemMap[key] || 0) + qty;
            });
          });

          const topItemKey = Object.entries(itemMap).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
          return {
            mode,
            revenue,
            billCount: periodSales.length,
            topItem: topItemKey ? (itemNameMap[topItemKey] || topItemKey) : 'No sales yet',
            topQty: topItemKey ? itemMap[topItemKey] : 0
          };
        };

        const [weekly, monthly, yearly] = await Promise.all([
          getPeriodMetrics('this-week'),
          getPeriodMetrics('this-month'),
          getPeriodMetrics('this-year')
        ]);

        setAnalytics({
          range: { start: startStr, end: endStr, mode: preset },
          kpis: {
            totalSales,
            totalPurchases,
            totalExpenses,
            normalExpensesTotal,
            salaryExpensesTotal,
            labourExpensesTotal,
            grossProfit,
            netProfit,
            receipts: totalCashIn,
            payments: totalCashOut,
            paymentsIn: paymentsAgg.in,
            paymentsOut: paymentsAgg.out,
            employeeInflow: employeeFlowAgg.in,
            employeeOutflow: employeeFlowAgg.out,
            netCashflow: totalCashIn - totalCashOut,
            salesCount: salesBills.length,
            purchasesCount: purchaseBills.length,
            avgSalesBill: salesBills.length ? totalSales / salesBills.length : 0,
            avgPurchaseBill: purchaseBills.length ? totalPurchases / purchaseBills.length : 0,
            totalItems: items.length,
            totalParties: parties.length
          },
          highlights: { weekly, monthly, yearly, custom: { revenue: totalSales, billCount: salesBills.length } },
          topItems: mostSoldItems,
          fastMovingItems,
          topParties,
          itemStockList,
          trend: revenueTrend
        });
      } catch (fetchError) {
        console.error('Failed to load business analytics:', fetchError);
        setError('Unable to load analytics. Please check data indexes and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [db, userId, isAuthReady, appId, companyId, activeRange, preset]);

  useEffect(() => {
    setItemsTablePage(1);
  }, [itemsSortBy, itemsSortOrder, itemsTablePageSize, analytics?.range?.start, analytics?.range?.end]);

  useEffect(() => {
    if (itemsTablePage > itemsTableTotalPages) {
      setItemsTablePage(itemsTableTotalPages);
    }
  }, [itemsTablePage, itemsTableTotalPages]);

  useEffect(() => {
    setTopPartiesPage(1);
  }, [topPartiesPageSize, analytics?.range?.start, analytics?.range?.end]);

  useEffect(() => {
    if (topPartiesPage > topPartiesTotalPages) setTopPartiesPage(topPartiesTotalPages);
  }, [topPartiesPage, topPartiesTotalPages]);

  useEffect(() => {
    setLowStockPage(1);
  }, [itemStockSortOrder, lowStockPageSize, analytics?.range?.start, analytics?.range?.end]);

  useEffect(() => {
    if (lowStockPage > itemStockTotalPages) setLowStockPage(itemStockTotalPages);
  }, [lowStockPage, itemStockTotalPages]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-50 to-purple-100 report-page-root max-w-full overflow-x-clip">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-full">
      <div className="bg-gradient-to-br from-white via-blue-50/40 to-purple-50/40 backdrop-blur-sm rounded-2xl shadow-xl border border-blue-200 p-4 sm:p-6 max-w-full overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 rounded-xl shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3h2v18h-2zM4 13h2v8H4zm14-6h2v14h-2zM7.5 9.5l3 2.5 5-5" />
                </svg>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-blue-800">Business Analytics</h1>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Simple view of sales, purchase, expenses and items.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full md:w-auto">
            <AppSelect
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              aria-label="Date range preset"
              sheetTitle="Date range"
              options={PRESET_OPTIONS}
              triggerClassName="w-full border border-blue-300 bg-blue-50 text-blue-800 rounded-lg pl-3 pr-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 text-left min-h-[44px] flex items-center justify-between touch-manipulation"
            />

            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              disabled={preset !== 'custom'}
              className="border border-blue-300 bg-blue-50 text-blue-800 rounded-lg pl-3 pr-8 py-2 text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              disabled={preset !== 'custom'}
              className="border border-blue-300 bg-blue-50 text-blue-800 rounded-lg pl-3 pr-8 py-2 text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mb-4 flex justify-stretch sm:justify-end">
          <button
            onClick={downloadCsv}
            disabled={!analytics}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm rounded-lg border border-blue-700 text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 disabled:opacity-60 font-semibold shadow-lg touch-manipulation min-h-[44px] sm:min-h-0"
          >
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-600">Loading analytics...</div>
        ) : null}
        {error ? (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        ) : null}

        {analytics && !loading ? (
          <>
            <div className="mb-6 text-sm text-gray-600">
              Period: <span className="font-semibold">{analytics.range.start}</span> to <span className="font-semibold">{analytics.range.end}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              <KpiCard title="Sales" value={formatMoney(analytics.kpis.totalSales)} tone="green" />
              <KpiCard title="Purchase" value={formatMoney(analytics.kpis.totalPurchases)} tone="blue" />
              <KpiCard
                title="Expenses"
                value={formatMoney(analytics.kpis.totalExpenses)}
                tone="orange"
                hint={`Operating ${formatMoney(analytics.kpis.normalExpensesTotal)} | Salary ${formatMoney(analytics.kpis.salaryExpensesTotal)} | Labour ${formatMoney(analytics.kpis.labourExpensesTotal)}`}
              />
              <KpiCard title="Net Profit" value={formatMoney(analytics.kpis.netProfit)} tone={analytics.kpis.netProfit >= 0 ? 'teal' : 'red'} />
              <KpiCard title="Gross Profit" value={formatMoney(analytics.kpis.grossProfit)} tone={analytics.kpis.grossProfit >= 0 ? 'teal' : 'red'} />
              <KpiCard
                title="Cash In"
                value={formatMoney(analytics.kpis.receipts)}
                tone="green"
                hint={`Payments ${formatMoney(analytics.kpis.paymentsIn)} | Employee ${formatMoney(analytics.kpis.employeeInflow)}`}
              />
              <KpiCard
                title="Cash Out"
                value={formatMoney(analytics.kpis.payments)}
                tone="purple"
                hint={`Payments ${formatMoney(analytics.kpis.paymentsOut)} | Employee ${formatMoney(analytics.kpis.employeeOutflow)}`}
              />
              <KpiCard title="Net Cashflow" value={formatMoney(analytics.kpis.netCashflow)} tone={analytics.kpis.netCashflow >= 0 ? 'teal' : 'red'} />
            </div>

            <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-indigo-50 mb-6">
              <h2 className="text-base font-semibold text-blue-700 mb-3">Cashflow Source Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-100 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-2">Cash In Sources</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-emerald-800">From Payments</span>
                      <span className="font-semibold text-emerald-900">{formatMoney(analytics.kpis.paymentsIn)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-emerald-800">From Employee Flow</span>
                      <span className="font-semibold text-emerald-900">{formatMoney(analytics.kpis.employeeInflow)}</span>
                    </div>
                    <div className="border-t border-emerald-200 pt-1 mt-2 flex items-center justify-between">
                      <span className="font-semibold text-emerald-900">Total In</span>
                      <span className="font-bold text-emerald-900">{formatMoney(analytics.kpis.receipts)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-100 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-purple-800 mb-2">Cash Out Sources</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-purple-800">From Payments</span>
                      <span className="font-semibold text-purple-900">{formatMoney(analytics.kpis.paymentsOut)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-purple-800">From Employee Flow</span>
                      <span className="font-semibold text-purple-900">{formatMoney(analytics.kpis.employeeOutflow)}</span>
                    </div>
                    <div className="border-t border-purple-200 pt-1 mt-2 flex items-center justify-between">
                      <span className="font-semibold text-purple-900">Total Out</span>
                      <span className="font-bold text-purple-900">{formatMoney(analytics.kpis.payments)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-indigo-50 lg:col-span-2">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Revenue Trend Chart</h2>
                {analytics.trend.length ? (
                  <SimpleVerticalBars
                    data={[...analytics.trend].reverse().slice(0, 8)}
                    valueKey="revenue"
                    labelKey="label"
                    color="from-blue-500 to-purple-600"
                    formatter={compactMoney}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No trend data available.</p>
                )}
              </div>

              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-purple-50">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Cash In vs Out</h2>
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-700 font-medium">Cash In</p>
                      <p className="text-sm font-semibold text-emerald-700">{formatMoney(analytics.kpis.receipts)}</p>
                    </div>
                    <SimpleBar
                      value={analytics.kpis.receipts}
                      maxValue={Math.max(analytics.kpis.receipts, analytics.kpis.payments, 1)}
                      color="bg-emerald-500"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-700 font-medium">Cash Out</p>
                      <p className="text-sm font-semibold text-purple-700">{formatMoney(analytics.kpis.payments)}</p>
                    </div>
                    <SimpleBar
                      value={analytics.kpis.payments}
                      maxValue={Math.max(analytics.kpis.receipts, analytics.kpis.payments, 1)}
                      color="bg-purple-500"
                    />
                  </div>
                  <div className="pt-2 border-t border-purple-100">
                    <p className="text-xs text-gray-600">Net</p>
                    <p className={`text-lg font-bold ${analytics.kpis.netCashflow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {formatMoney(analytics.kpis.netCashflow)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-cyan-50 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h2 className="text-base font-semibold text-blue-700">Top Selling Items Chart</h2>
                <div className="flex items-center gap-2">
                  <AppSelect
                    value={itemsSortBy}
                    onChange={(e) => setItemsSortBy(e.target.value)}
                    aria-label="Sort items"
                    sheetTitle="Sort items"
                    options={[
                      { value: 'qty', label: 'Sort: Quantity' },
                      { value: 'revenue', label: 'Sort: Revenue' },
                      { value: 'name', label: 'Sort: Name' },
                    ]}
                    triggerClassName="text-xs border border-blue-200 rounded-md pl-2 pr-2 py-1 bg-white text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                  />
                  <button
                    onClick={() => setItemsSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                    className="text-xs border border-blue-200 rounded-md px-2 py-1 bg-white text-blue-700"
                  >
                    {itemsSortOrder === 'asc' ? 'Asc' : 'Desc'}
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {sortedTopItems.length ? sortedTopItems.slice(0, visibleTopSellingChartCount).map((item) => (
                  <div key={`chart-${item.itemKey}`} className="bg-white/70 border border-blue-100 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-800 truncate pr-2">{item.name}</p>
                      <p className="text-xs font-semibold text-blue-700">{item.qty.toFixed(2)} qty</p>
                    </div>
                    <SimpleBar
                      value={item.qty}
                      maxValue={Math.max(...sortedTopItems.map((it) => parseNumber(it.qty || 0)), 0)}
                      color="bg-cyan-500"
                    />
                  </div>
                )) : (
                  <p className="text-sm text-gray-500">No item sales data available.</p>
                )}
                {sortedTopItems.length > 6 && (
                  <div className="pt-1">
                    {visibleTopSellingChartCount < sortedTopItems.length ? (
                      <button
                        onClick={() => setVisibleTopSellingChartCount((prev) => Math.min(prev + 6, sortedTopItems.length))}
                        className="text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        View More
                      </button>
                    ) : (
                      <button
                        onClick={() => setVisibleTopSellingChartCount(6)}
                        className="text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        View Less
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-blue-50">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Revenue (Week / Month / Year / Selected)</h2>
                <div className="space-y-3">
                  {[
                    { label: 'This Week', data: analytics.highlights.weekly },
                    { label: 'This Month', data: analytics.highlights.monthly },
                    { label: 'This Year', data: analytics.highlights.yearly },
                    { label: 'Selected Range', data: analytics.highlights.custom }
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 border border-blue-100">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{row.label}</p>
                        <p className="text-xs text-gray-500">
                          Bills: {row.data.billCount} {row.data.topItem ? `| Top item: ${row.data.topItem}` : ''}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-blue-700">{formatMoney(row.data.revenue)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-indigo-50">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Coverage</h2>
                <div className="grid grid-cols-2 gap-3">
                  <KpiCard title="Sales Bills" value={analytics.kpis.salesCount} tone="blue" />
                  <KpiCard title="Purchase Bills" value={analytics.kpis.purchasesCount} tone="blue" />
                  <KpiCard title="Avg Sales Bill" value={formatMoney(analytics.kpis.avgSalesBill)} tone="green" />
                  <KpiCard title="Avg Purchase Bill" value={formatMoney(analytics.kpis.avgPurchaseBill)} tone="purple" />
                  <KpiCard title="Items Catalog" value={analytics.kpis.totalItems} tone="orange" />
                  <KpiCard title="Parties" value={analytics.kpis.totalParties} tone="teal" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-cyan-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h2 className="text-base font-semibold text-blue-700">Most Sold Items</h2>
                  <div className="flex items-center gap-2">
                    <AppSelect
                      value={itemsSortBy}
                      onChange={(e) => setItemsSortBy(e.target.value)}
                      aria-label="Sort most sold items"
                      sheetTitle="Sort items"
                      options={[
                        { value: 'qty', label: 'Sort: Quantity' },
                        { value: 'revenue', label: 'Sort: Revenue' },
                        { value: 'name', label: 'Sort: Name' },
                      ]}
                      triggerClassName="text-xs border border-blue-200 rounded-md pl-2 pr-2 py-1 bg-white text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                    />
                    <button
                      onClick={() => setItemsSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                      className="text-xs border border-blue-200 rounded-md px-2 py-1 bg-white text-blue-700"
                    >
                      {itemsSortOrder === 'asc' ? 'Asc' : 'Desc'}
                    </button>
                  </div>
                </div>
                <div className="md:hidden space-y-2">
                  {sortedTopItems.length ? paginatedTopItems.map((item) => (
                    <div key={item.itemKey} className="flex items-center justify-between gap-3 p-3 bg-white/80 rounded-lg border border-blue-100">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-800 truncate">{item.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Qty: {item.qty.toFixed(2)}</div>
                      </div>
                      <div className="text-right font-semibold text-green-700 shrink-0 tabular-nums">{formatMoney(item.revenue)}</div>
                    </div>
                  )) : (
                    <div className="py-3 text-center text-gray-500 text-sm">No item sales in selected period.</div>
                  )}
                </div>
                <div className="hidden md:block report-scroll-x">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2 pr-2">Item</th>
                        <th className="py-2 pr-2 text-right">Qty Sold</th>
                        <th className="py-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTopItems.length ? paginatedTopItems.map((item) => (
                        <tr key={item.itemKey} className="border-b last:border-b-0">
                          <td className="py-2 pr-2 text-gray-800">{item.name}</td>
                          <td className="py-2 pr-2 text-right font-medium">{item.qty.toFixed(2)}</td>
                          <td className="py-2 text-right font-semibold text-green-700">{formatMoney(item.revenue)}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={3} className="py-3 text-center text-gray-500">No item sales in selected period.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {sortedTopItems.length > 0 && (
                  <div className="mt-3 border-t border-blue-100 pt-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <span>Show</span>
                        <AppSelect
                          value={String(itemsTablePageSize)}
                          onChange={(e) => setItemsTablePageSize(parseInt(e.target.value, 10))}
                          aria-label="Items page size"
                          sheetTitle="Show entries"
                          options={[
                            { value: '10', label: '10' },
                            { value: '25', label: '25' },
                            { value: '50', label: '50' },
                            { value: '100', label: '100' },
                          ]}
                          triggerClassName="border border-blue-200 rounded pl-2 pr-2 py-1 bg-white text-xs text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                        />
                        <span>entries</span>
                      </div>

                      <div>
                        Showing {itemsTableStartIndex} to {itemsTableEndIndex} of {sortedTopItems.length} entries
                      </div>

                      <div className="flex items-center gap-1 flex-wrap">
                        <button
                          disabled={itemsTablePage === 1}
                          onClick={() => setItemsTablePage(1)}
                          className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50"
                        >
                          First
                        </button>
                        <button
                          disabled={itemsTablePage === 1}
                          onClick={() => setItemsTablePage((prev) => Math.max(1, prev - 1))}
                          className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50"
                        >
                          Prev
                        </button>

                        {getVisiblePageNumbers().map((pageNo) => (
                          <button
                            key={pageNo}
                            onClick={() => setItemsTablePage(pageNo)}
                            className={`px-2 py-1 rounded border ${
                              pageNo === itemsTablePage
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white border-blue-200'
                            }`}
                          >
                            {pageNo}
                          </button>
                        ))}

                        <button
                          disabled={itemsTablePage === itemsTableTotalPages}
                          onClick={() => setItemsTablePage((prev) => Math.min(itemsTableTotalPages, prev + 1))}
                          className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50"
                        >
                          Next
                        </button>
                        <button
                          disabled={itemsTablePage === itemsTableTotalPages}
                          onClick={() => setItemsTablePage(itemsTableTotalPages)}
                          className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-indigo-50">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Revenue Trend</h2>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {analytics.trend.length ? analytics.trend.map((point) => (
                    <div key={point.label} className="p-3 rounded-lg bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-800">{point.label}</p>
                        <p className="text-xs text-gray-500">Bills: {point.bills}</p>
                      </div>
                      <SimpleBar
                        value={point.revenue}
                        maxValue={analytics.trend[0]?.revenue || 0}
                        color="bg-blue-600"
                      />
                      <div className="mt-1 text-right">
                        <p className="text-sm font-semibold text-blue-700">{formatMoney(point.revenue)}</p>
                      </div>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500">No trend data available for this range.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-violet-50">
                <h2 className="text-base font-semibold text-blue-700 mb-3">Top Parties</h2>
                <div className="space-y-3">
                  {analytics.topParties.length ? paginatedTopParties.map((party) => (
                    <div key={party.id} className="p-2 rounded bg-gradient-to-r from-indigo-100 via-blue-100 to-purple-100 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-800">{party.name}</p>
                        <p className="text-sm font-semibold text-green-700">{formatMoney(party.revenue)}</p>
                      </div>
                      <SimpleBar
                        value={party.revenue}
                        maxValue={analytics.topParties[0]?.revenue || 0}
                        color="bg-blue-600"
                      />
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500">No party sales data in this range.</p>
                  )}
                  {analytics.topParties.length > 0 && (
                    <div className="pt-2 border-t border-blue-100 text-xs text-gray-700">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span>Show</span>
                          <AppSelect
                            value={String(topPartiesPageSize)}
                            onChange={(e) => setTopPartiesPageSize(parseInt(e.target.value, 10))}
                            aria-label="Top parties page size"
                            sheetTitle="Show entries"
                            options={[
                              { value: '10', label: '10' },
                              { value: '25', label: '25' },
                              { value: '50', label: '50' },
                            ]}
                            triggerClassName="border border-blue-200 rounded pl-2 pr-2 py-1 bg-white text-xs text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                          />
                          <span>entries</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button disabled={topPartiesPage === 1} onClick={() => setTopPartiesPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50">Prev</button>
                          <span>{topPartiesPage} / {topPartiesTotalPages}</span>
                          <button disabled={topPartiesPage === topPartiesTotalPages} onClick={() => setTopPartiesPage((p) => Math.min(topPartiesTotalPages, p + 1))} className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50">Next</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-purple-50">
                <h2 className="text-base font-semibold text-blue-700 mb-1">Fast Moving Items</h2>
                <p className="text-xs text-gray-500 mb-3">Qty sold in selected period</p>
                <div className="space-y-3">
                  {analytics.fastMovingItems.length ? analytics.fastMovingItems.slice(0, visibleFastMovingCount).map((item) => (
                    <div key={item.itemKey} className="p-2 rounded bg-gradient-to-r from-purple-100 via-indigo-100 to-blue-100 border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-800">{item.name}</p>
                        <p className="text-sm font-semibold text-blue-700">{formatNumber(item.qty)}</p>
                      </div>
                      <SimpleBar
                        value={item.qty}
                        maxValue={analytics.fastMovingItems[0]?.qty || 0}
                        color="bg-purple-600"
                      />
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500">No fast moving items found.</p>
                  )}
                  {analytics.fastMovingItems.length > 5 && (
                    <div className="pt-1">
                      {visibleFastMovingCount < analytics.fastMovingItems.length ? (
                        <button
                          onClick={() => setVisibleFastMovingCount((prev) => Math.min(prev + 5, analytics.fastMovingItems.length))}
                          className="text-sm font-medium text-blue-700 hover:text-blue-900"
                        >
                          View More
                        </button>
                      ) : (
                        <button
                          onClick={() => setVisibleFastMovingCount(5)}
                          className="text-sm font-medium text-blue-700 hover:text-blue-900"
                        >
                          View Less
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-blue-200 p-4 shadow-sm bg-gradient-to-br from-white to-orange-50">
                <h2 className="text-base font-semibold text-blue-700 mb-1">Item Stock</h2>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-xs text-gray-500">
                    All items · in hand from all purchases & sales ({analytics.itemStockList?.length || 0} items)
                  </p>
                  <div className="flex items-center gap-2">
                    <AppSelect
                      value={itemStockSortOrder}
                      onChange={(e) => setItemStockSortOrder(e.target.value)}
                      aria-label="Item stock sort"
                      sheetTitle="Stock sort"
                      options={[
                        { value: 'desc', label: 'Stock: High to Low' },
                        { value: 'asc', label: 'Stock: Low to High' },
                      ]}
                      triggerClassName="border border-blue-200 rounded pl-2 pr-2 py-1 bg-white text-xs text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  {analytics.itemStockList?.length ? paginatedItemStock.map((item) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-2 rounded border ${
                        item.status === 'negative'
                          ? 'bg-gradient-to-r from-red-200 via-rose-100 to-orange-100 border-red-300'
                          : item.status === 'low'
                            ? 'bg-gradient-to-r from-red-100 via-orange-100 to-amber-100 border-red-200'
                            : 'bg-gradient-to-r from-emerald-50 via-blue-50 to-indigo-50 border-blue-100'
                      }`}
                    >
                      <div>
                        <p className="text-sm text-gray-800">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          Reorder: {formatNumber(item.reorderLevel)}
                          {item.status === 'negative' ? ' · Negative' : item.status === 'low' ? ' · Low' : ' · Normal'}
                        </p>
                      </div>
                      <p className={`text-sm font-bold ${
                        item.status === 'negative'
                          ? 'text-red-900'
                          : item.status === 'low'
                            ? 'text-red-700'
                            : 'text-emerald-700'
                      }`}>
                        {formatNumber(item.stock)}
                      </p>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500">No items found.</p>
                  )}
                  {analytics.itemStockList?.length > 0 && (
                    <div className="pt-2 border-t border-blue-100 text-xs text-gray-700">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span>Show</span>
                          <AppSelect
                            value={String(lowStockPageSize)}
                            onChange={(e) => setLowStockPageSize(parseInt(e.target.value, 10))}
                            aria-label="Item stock page size"
                            sheetTitle="Show entries"
                            options={[
                              { value: '10', label: '10' },
                              { value: '25', label: '25' },
                              { value: '50', label: '50' },
                              { value: '100', label: '100' },
                            ]}
                            triggerClassName="border border-blue-200 rounded pl-2 pr-2 py-1 bg-white text-xs text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                          />
                          <span>entries</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button disabled={lowStockPage === 1} onClick={() => setLowStockPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50">Prev</button>
                          <span>{lowStockPage} / {itemStockTotalPages}</span>
                          <button disabled={lowStockPage === itemStockTotalPages} onClick={() => setLowStockPage((p) => Math.min(itemStockTotalPages, p + 1))} className="px-2 py-1 rounded border border-blue-200 bg-white disabled:opacity-50">Next</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
    </div>
  );
};

export default BusinessAnalytics;
